import styles from "./Support.module.scss";
import React, {useState} from "react";
import {HeaderMenu} from "../../common/HeaderMenu";
import {Content, ContentFixed, ContentScroll} from "../../common/Content";
import {useTranslation} from "react-i18next";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "../../common/Button";
import {createSupport} from "../../api/ApiSupport";
import useValidator from "../../util/useValidator";
import useToast from "../../util/useToast";
import useNetwork from "../../util/useNetwork";
import {getLastSupportTime, setLastSupportTime} from "../../util/localStorage.service";
import moment from "moment";
import {Alert} from "react-bootstrap";

const Support = () => {
  const {t} = useTranslation("support");
  const {showToastSuccess} = useToast();
  const {handleNetworkError} = useNetwork();
  const validate = useValidator();

  const [editedSupport, setEditedSupport] = useState({subject: "", body: "", errors: {}});
  const [saving, setSaving] = useState(false);

  const updateEditedSupport = field => event => {
    const values = {};

    switch (field) {
      default:
        values[field] = event.target.value;
        break;
    }

    setEditedSupport(prev => ({...prev, ...values}));
  };

  const getErrors = values => {
    const errors = {};

    if (/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(getLastSupportTime())) {
      const lastSupportTime = moment(getLastSupportTime());

      if (lastSupportTime.isAfter(moment().subtract(1, "hour"))) {
        errors.time = "support_editor_error_time_wait";
      }
    }

    if (!errors.time) {
      if (!values.subject.trim().length) {
        errors.subject = "support_editor_error_subject_must_be_filled";
      }

      if (!values.body.trim().length) {
        errors.body = "support_editor_error_body_must_be_filled";
      }
    }

    return errors;
  };

  const submit = e => {
    e.preventDefault();

    if (saving) {
      return;
    }

    setSaving(true);

    const values = {subject: editedSupport.subject, body: editedSupport.body};

    return validate(getErrors(values))
      .then(() => createSupport(values))
      .then(() => {
        showToastSuccess(t("support_added"));

        setEditedSupport({subject: "", body: "", errors: {}});

        setLastSupportTime(moment().format("YYYY-MM-DD HH:mm:ss"));
      })
      .catch(error => {
        handleNetworkError(error, {
          400: err => setEditedSupport(prev => ({...prev, errors: err.response.data}))
        });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Content>
      <ContentFixed>
        <HeaderMenu title={t("header_title_support")}/>
      </ContentFixed>

      <ContentScroll>
        <div className={styles.editor}>
          <Form onSubmit={submit}>
            <p className={styles.help}>{t("help_text")}</p>

            {editedSupport.errors.time && <Alert variant="danger">{t(editedSupport.errors.time)}</Alert>}

            <Form.Group as={Row} className="mb-2" controlId="support-subject">
              <Form.Label column sm={3}>{t("support_editor_field_subject")}</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={editedSupport.subject}
                  onChange={updateEditedSupport("subject")}
                  maxLength="50"
                  isInvalid={editedSupport.errors.subject}
                />

                <Form.Control.Feedback type="invalid">{t(editedSupport.errors.subject)}</Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-2" controlId="support-body">
              <Form.Label column sm={3}>{t("support_editor_field_body")}</Form.Label>
              <Col sm={9}>
                <Form.Control
                  as="textarea"
                  value={editedSupport.body}
                  onChange={updateEditedSupport("body")}
                  isInvalid={editedSupport.errors.body}
                  rows={10}
                />

                <Form.Control.Feedback type="invalid">{t(editedSupport.errors.body)}</Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Row>
              <Col sm={3} />
              <Col sm={9}>
                <Button type="submit" variant="primary" disabled={saving}>{t("support_editor_button_submit")}</Button>
              </Col>
            </Row>
          </Form>
        </div>
      </ContentScroll>
    </Content>
  );
};

export default Support;
